/**
 * Gift with purchase (GWP) — cart layer.
 *
 * Reads its config from the #gwp-config JSON in layout/theme.liquid (theme settings
 * under "Gift with purchase") and keeps the cart in one of two valid states:
 *
 *   - trigger product in cart  ->  exactly one gift line, quantity 1
 *   - no trigger (or disabled) ->  no gift line
 *
 * The gift line is tagged with the private line property "_gwp" so it can be told
 * apart from the same product added normally. Pricing is NOT done here: a
 * Shopify automatic "Buy X get Y" discount must zero the gift. As a safety net, if
 * the gift is not free right after being added, it is removed again and no retry
 * happens for a while, so a customer is never charged for it.
 */
(() => {
  const configElement = document.getElementById('gwp-config');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    console.error('[GWP] invalid config', error);
    return;
  }

  const GIFT_PROPERTY = '_gwp';
  const BLOCK_KEY = 'gwp:blocked-until';
  const BLOCK_MINUTES = 15;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const triggerIds = (config.triggerProductIds || []).map(Number);
  const giftVariantId = config.giftVariantId ? Number(config.giftVariantId) : null;
  const giftProductId = config.giftProductId ? Number(config.giftProductId) : null;

  const isGiftLine = (item) => Boolean(item.properties && item.properties[GIFT_PROPERTY]);
  const isTriggerLine = (item) => !isGiftLine(item) && triggerIds.includes(Number(item.product_id));
  const cartHasTrigger = (cart) => cart.items.some(isTriggerLine);

  const isBlocked = () => {
    try {
      const until = Number(sessionStorage.getItem(BLOCK_KEY) || 0);
      return until > Date.now();
    } catch (error) {
      return false;
    }
  };

  const block = () => {
    try {
      sessionStorage.setItem(BLOCK_KEY, String(Date.now() + BLOCK_MINUTES * 60 * 1000));
    } catch (error) {
      /* storage unavailable: we simply retry on the next cart change */
    }
  };

  const fetchCart = async () => (await fetch(`${root}cart.js`)).json();

  /**
   * Ask the theme which sections (cart drawer, etc.) should be re-rendered with
   * the request, exactly like the stock product form does.
   */
  const bundledSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  };

  const post = async (path, body) => {
    const response = await fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ ...body, sections: bundledSections() }),
    });
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json.description || json.message || `${path} failed`);
    }
    return json;
  };

  /**
   * Tell the theme the cart changed so the drawer, count bubble and free shipping
   * bar re-render. "gwp: true" lets this script ignore its own events.
   */
  const broadcast = (cart, baseEvent) => {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent, cart, gwp: true } })
    );
  };

  const removeLines = async (lines) => {
    let cart = null;
    for (const line of lines) {
      try {
        cart = await post('cart/change.js', { id: line.key, quantity: 0 });
      } catch (error) {
        // The line may already be gone (e.g. removed by another cart update in flight).
        console.warn('[GWP] could not remove line', line.key, error.message);
      }
    }
    return cart || fetchCart();
  };

  /** Returns true when the cart was modified. */
  const reconcile = async (cart) => {
    cart = cart || (await fetchCart());

    const giftLines = cart.items.filter(isGiftLine);
    const wantGift = Boolean(config.enabled && giftVariantId && cartHasTrigger(cart));

    if (!wantGift) {
      if (giftLines.length === 0) return false;
      // "line-item:change" keeps the drawer's delayed re-render, so ours lands after the
      // customer's own change and the final DOM reflects the gift being gone.
      broadcast(await removeLines(giftLines), 'line-item:change');
      return true;
    }

    if (giftLines.length === 0) {
      if (isBlocked()) return false;

      const added = await post('cart/add.js', {
        id: giftVariantId,
        quantity: 1,
        properties: { [GIFT_PROPERTY]: '1' },
      });

      const freshCart = await fetchCart();
      freshCart.sections = added.sections;

      const giftLine = freshCart.items.find(isGiftLine);
      if (giftLine && giftLine.final_line_price > 0) {
        // The automatic discount is not active: back the gift out and stop retrying.
        console.warn('[GWP] gift was not discounted to 0, removing it. Check the automatic discount in Shopify admin.');
        block();
        broadcast(await removeLines([giftLine]), 'line-item:change');
        return true;
      }

      broadcast(freshCart, 'variant:add');
      return true;
    }

    // One gift per order: keep a single line at quantity 1.
    const [keep, ...extra] = giftLines;
    let updatedCart = null;
    if (extra.length > 0) {
      updatedCart = await removeLines(extra);
    }
    if (keep.quantity !== 1) {
      updatedCart = await post('cart/change.js', { id: keep.key, quantity: 1 });
    }
    if (updatedCart) {
      broadcast(updatedCart, 'line-item:change');
      return true;
    }
    return false;
  };

  let busy = false;
  let pending = false;

  const run = async (cart) => {
    if (busy) {
      pending = true;
      return;
    }
    busy = true;
    try {
      const changed = await reconcile(cart);
      // The cart page does full reloads on every change, so match that.
      if (changed && window.themeVariables?.settings?.pageType === 'cart') {
        window.location.reload();
      }
    } catch (error) {
      console.error('[GWP]', error);
    } finally {
      busy = false;
      if (pending) {
        pending = false;
        run();
      }
    }
  };

  // Theme events (product form, quantity changes, cart refresh).
  document.addEventListener('cart:change', (event) => {
    if (event.detail?.gwp) return;
    run(event.detail?.cart);
  });
  document.addEventListener('cart:refresh', () => run());
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) run();
  });

  // Custom paths in this theme (recommendation "+Add" buttons, discount links) update
  // the drawer manually without firing cart:change, so also reconcile whenever the
  // drawer opens and after the global helper they use.
  const hookManualPaths = () => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      new MutationObserver(() => {
        if (drawer.hasAttribute('open')) run();
      }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
    }

    const original = window.updateCartDrawer;
    if (typeof original === 'function' && !original._gwpWrapped) {
      const wrapped = async function (...args) {
        const result = await original.apply(this, args);
        run();
        return result;
      };
      wrapped._gwpWrapped = true;
      window.updateCartDrawer = wrapped;
    }
  };

  const start = () => {
    hookManualPaths();
    run();
  };

  if (document.readyState === 'complete') {
    start();
  } else {
    window.addEventListener('load', start, { once: true });
  }
})();
